import * as THREE from "three";
import Experience from "../../../Experience.js";

export default class WaterSurface {
    constructor(bounds) {
        this.experience = new Experience();
        this.debug = this.experience.debug;

        this.bounds = bounds;

        const surfaceColor = new THREE.Color("#1e4f9f");
        this.params = {
            color: surfaceColor.getHex(THREE.SRGBColorSpace),
        };

        this.uniforms = {
            uFadeRadius: { value: Math.min(bounds.width, bounds.depth) * 0.8 },
            uFadeSpread: { value: Math.min(bounds.width, bounds.depth) * 0.2 },
            uSunDirection: { value: new THREE.Vector3() },
            uSunColor: { value: new THREE.Color("#ffffff") },
            uSunGlareIntensity: { value: 0.2 },
        };
        this.updateUniforms();

        const geometry = new THREE.PlaneGeometry(
            bounds.width * 2,
            bounds.depth * 2,
        );
        this.material = new THREE.MeshBasicMaterial({
            color: surfaceColor,
            side: THREE.BackSide,
        });

        this.material.onBeforeCompile = (shader) => {
            shader.uniforms.uFadeRadius = this.uniforms.uFadeRadius;
            shader.uniforms.uFadeSpread = this.uniforms.uFadeSpread;
            shader.uniforms.uSunDirection = this.uniforms.uSunDirection;
            shader.uniforms.uSunColor = this.uniforms.uSunColor;
            shader.uniforms.uSunGlareIntensity =
                this.uniforms.uSunGlareIntensity;

            shader.vertexShader = shader.vertexShader.replace(
                "#include <common>",
                `#include <common>
                varying vec3 vWorldPosition;`,
            );

            shader.vertexShader = shader.vertexShader.replace(
                "#include <worldpos_vertex>",
                `#include <worldpos_vertex>
                vWorldPosition = worldPosition.xyz;`,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <common>",
                `#include <common>
                uniform float uFadeRadius;
                uniform float uFadeSpread;
                uniform vec3 uSunDirection;
                uniform vec3 uSunColor;
                uniform float uSunGlareIntensity;
                varying vec3 vWorldPosition;`,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <color_fragment>",
                `#include <color_fragment>
                float finalCaustic = 0.0;
                #ifdef USE_CAUSTICS
                finalCaustic = getSurfaceCaustic(vCausticWorldPosition.xz * uCausticScale, uTime * 0.001);
                diffuseColor *= (1.0 + finalCaustic * uCausticStrength);
                #endif

                // Sun Glare
                vec3 viewDir = normalize(vWorldPosition - cameraPosition);
                vec3 sunDir = normalize(uSunDirection);
                float sunGlare = max(dot(viewDir, sunDir), 0.0);
                
                // Efficiently calculate power of 10 using squaring: x^2 -> x^4 -> x^8 * x^2 = x^10
                float sg2 = sunGlare * sunGlare;
                float sg4 = sg2 * sg2;
                float sg8 = sg4 * sg4;
                sunGlare = sg8 * sg2;
                
                // Add glare, intensified by caustics for a sparkling effect
                float glareBoost = 1.0;
                #ifdef USE_CAUSTICS
                glareBoost += finalCaustic * uCausticStrength;
                #endif
                vec3 sunGlareColor = uSunColor * sunGlare * uSunGlareIntensity * glareBoost;
                diffuseColor.rgb += sunGlareColor;
                // diffuseColor.rgb *= mix(vec3(1.0), sunGlareColor, sunGlare);
                `,
            );

            shader.fragmentShader = shader.fragmentShader.replace(
                "#include <fog_fragment>",
                `#include <fog_fragment>
                float dist = length(vWorldPosition.xz);
                float alpha = smoothstep(uFadeRadius, uFadeRadius + uFadeSpread, dist);
                gl_FragColor.rgb = mix(gl_FragColor.rgb, fogColor, alpha);`,
            );
        };

        this.mesh = new THREE.Mesh(geometry, this.material);
        this.mesh.rotation.x = -Math.PI / 2;
        this.mesh.position.y = bounds.max.y + 10;
        this.mesh.receiveShadow = false;

        if (this.debug.active) {
            this.setDebug();
        }
    }

    // copy sun direction and color from the sun light in the scene
    updateUniforms() {
        if (this.experience.world.lights) {
            const sunLight = this.experience.world.lights.sunLight;
            this.uniforms.uSunDirection.value
                .copy(sunLight.position)
                .normalize();
            this.uniforms.uSunColor.value
                .copy(sunLight.color)
                .multiplyScalar(sunLight.intensity);
        }
    }

    setDebug() {
        this.debugFolder = this.debug.ui.addFolder("Water Surface");

        this.debugFolder.addColor(this.params, "color").onChange((v) => {
            this.material.color.setHex(v, THREE.SRGBColorSpace);
        });

        this.debugFolder
            .add(this.uniforms.uSunGlareIntensity, "value")
            .min(0)
            .max(10)
            .step(0.1)
            .name("Sun Glare Intensity");
    }
}
